import { useEffect, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { cn } from '../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../styling/stylingHelper';
import { uploadCommunicationImage, communicationInlineImageBytes, MAX_COMMUNICATION_INLINE_BYTES, type ProductUpdateContent, type ProductUpdateSection } from '../../../../services/admin/adminService';
import { PRODUCT_UPDATE_PRESETS, type ProductUpdatePreset, type PresetListType, type PresetLanguage } from './productUpdatePresets';

/**
 * Chapter-based composer for the rich product update email. The email's shape is
 * standardized: opening block (eyebrow → hero title → intro → hero image → CTA
 * button) → N chapters (each: title, optional body, optional list, optional
 * image, divider auto-inserted before it) → closing panel → sign-off.
 *
 * The chapters compile down to the server's flat `ProductUpdateSection[]` wire
 * format on every change, so the server model, validation and renderer are
 * untouched — this component only removes authoring decisions, not layout
 * capability. One feature link feeds both the primary button and the closing
 * panel; leave it empty to send an update without any linked page.
 *
 * Named presets (one per announced feature, see productUpdatePresets.ts) prefill
 * everything including the card's Title — only the screenshots stay manual.
 */

type ListType = PresetListType;

interface Chapter {
  key: number;
  title: string;
  body: string;
  listType: ListType;
  /** One item per line; steps may prefix a bold lead-in with "Lead | rest of the step". */
  itemsText: string;
  imageUrl: string;
  imageAlt: string;
  uploading: boolean;
  uploadError: string | null;
}

const LIST_TYPES: ListType[] = ['none', 'steps', 'bulletsBoxed', 'bulletsPlain'];

const LIST_TYPE_LABEL_KEYS: Record<ListType, string> = {
  none: 'adminTesting.templatedEmail.composer.listNone',
  steps: 'adminTesting.templatedEmail.composer.listSteps',
  bulletsBoxed: 'adminTesting.templatedEmail.composer.listBulletsBoxed',
  bulletsPlain: 'adminTesting.templatedEmail.composer.listBulletsPlain',
};

function emptyChapter(key: number): Chapter {
  return { key, title: '', body: '', listType: 'none', itemsText: '', imageUrl: '', imageAlt: '', uploading: false, uploadError: null };
}

/** "Lead | text" lines → the steps payload; plain lines → bullet items. */
function parseItems(itemsText: string, withLead: boolean): { lead?: string; text: string }[] {
  return itemsText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const pipe = withLead ? line.indexOf('|') : -1;
      if (pipe > 0) {
        return { lead: line.slice(0, pipe).trim(), text: line.slice(pipe + 1).trim() };
      }
      return { text: line };
    })
    .filter((item) => item.text);
}

/** One chapter → its flat sections (empty slots skipped; empty chapter → nothing). */
function compileChapter(chapter: Chapter): ProductUpdateSection[] {
  const parts: ProductUpdateSection[] = [];
  if (chapter.title.trim()) parts.push({ type: 'sectionTitle', text: chapter.title.trim() });
  if (chapter.body.trim()) parts.push({ type: 'paragraph', text: chapter.body.trim() });
  if (chapter.listType !== 'none') {
    const items = parseItems(chapter.itemsText, chapter.listType === 'steps');
    if (items.length > 0) {
      parts.push(
        chapter.listType === 'steps'
          ? { type: 'numberedSteps', items }
          : { type: 'bulletList', items: items.map((i) => i.text), boxed: chapter.listType === 'bulletsBoxed' }
      );
    }
  }
  if (chapter.imageUrl.trim()) {
    parts.push({ type: 'image', url: chapter.imageUrl.trim(), alt: chapter.imageAlt.trim() || undefined });
  }
  return parts.length > 0 ? [{ type: 'divider' }, ...parts] : [];
}

export function ProductUpdateComposer({ t, onChange, onApplyTitle }: {
  t: TFunction;
  onChange: (content: ProductUpdateContent | null) => void;
  /** A preset also prefills the card's Title field (the email's hero title). */
  onApplyTitle: (title: string) => void;
}) {
  // Language of the server-owned strings (greeting, sign-off, footer) AND of the
  // preset copy that gets applied. The admin's own edits are language-agnostic.
  const [language, setLanguage] = useState<PresetLanguage>('en');
  const [eyebrow, setEyebrow] = useState('New feature');
  const [preheader, setPreheader] = useState('');
  const [intro, setIntro] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [heroImageAlt, setHeroImageAlt] = useState('');
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroUploadError, setHeroUploadError] = useState<string | null>(null);
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaLabel, setCtaLabel] = useState('Check it out →');
  const [chapters, setChapters] = useState<Chapter[]>([emptyChapter(1)]);
  const [closingHeadline, setClosingHeadline] = useState('');
  const [closingButtonLabel, setClosingButtonLabel] = useState('');
  const nextKey = useRef(2);
  const imagesBusy = heroUploading || chapters.some(c => c.uploading);
  const imageFailed = Boolean(heroUploadError) || chapters.some(c => c.uploadError);
  const inlineImagesTooLarge = communicationInlineImageBytes(heroImageUrl)
    + chapters.reduce((bytes, chapter) => bytes + communicationInlineImageBytes(chapter.imageUrl), 0)
    > MAX_COMMUNICATION_INLINE_BYTES;

  useEffect(() => {
    // Never send a partial update while an image is preparing or has failed validation.
    if (imagesBusy || imageFailed || inlineImagesTooLarge) {
      onChange(null);
      return;
    }
    const sections: ProductUpdateSection[] = [];
    if (heroImageUrl.trim()) {
      sections.push({ type: 'image', url: heroImageUrl.trim(), alt: heroImageAlt.trim() || undefined });
    }
    const link = ctaUrl.trim();
    if (link && ctaLabel.trim()) {
      sections.push({ type: 'button', label: ctaLabel.trim(), url: link });
    }
    chapters.forEach((chapter) => sections.push(...compileChapter(chapter)));

    if (sections.length === 0) {
      onChange(null);
      return;
    }
    // A half-filled closing panel means the admin is mid-edit — hold the send.
    const closingFilled = [closingHeadline.trim(), closingButtonLabel.trim()].filter(Boolean).length;
    if (link && closingFilled === 1) {
      onChange(null);
      return;
    }
    const closingPanel =
      link && closingFilled === 2
        ? { headline: closingHeadline.trim(), buttonLabel: closingButtonLabel.trim(), url: link }
        : undefined;

    onChange({
      language,
      eyebrow: eyebrow.trim() || undefined,
      preheader: preheader.trim() || undefined,
      intro: intro.trim() || undefined,
      sections,
      closingPanel,
    });
  }, [language, eyebrow, preheader, intro, heroImageUrl, heroImageAlt, ctaUrl, ctaLabel, chapters, closingHeadline, closingButtonLabel, onChange, imagesBusy, imageFailed, inlineImagesTooLarge]);

  const applyPreset = (preset: ProductUpdatePreset) => {
    // Don't silently wipe copy the admin already wrote.
    const hasContent = intro.trim() !== '' || chapters.some((c) => c.title.trim() || c.body.trim() || c.itemsText.trim());
    if (hasContent && !window.confirm(t('adminTesting.templatedEmail.composer.presetOverwriteConfirm'))) return;

    const copy = preset.copy[language];
    onApplyTitle(copy.title);
    setEyebrow(copy.eyebrow);
    setPreheader(copy.preheader);
    setIntro(copy.intro);
    setHeroImageUrl('');
    setHeroUploadError(null);
    setHeroImageAlt(copy.heroImageAlt);
    setCtaUrl(preset.ctaUrl);
    setCtaLabel(copy.ctaLabel);
    setChapters(copy.chapters.map((chapter) => ({ ...chapter, imageUrl: '', key: nextKey.current++, uploading: false, uploadError: null })));
    setClosingHeadline(copy.closingHeadline);
    setClosingButtonLabel(copy.closingButtonLabel);
  };

  const updateChapter = (key: number, patch: Partial<Chapter>) => {
    setChapters((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  };

  const moveChapter = (key: number, direction: -1 | 1) => {
    setChapters((prev) => {
      const index = prev.findIndex((c) => c.key === key);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const uploadImage = async (file: File, apply: (url: string) => void, setBusy: (busy: boolean) => void, setError: (error: string | null) => void) => {
    onChange(null);
    try {
      setError(null);
      apply('');
      setBusy(true);
      apply(await uploadCommunicationImage(file));
    } catch (err: any) {
      console.error('Failed to prepare communication image', err);
      setError(t(err.message, { defaultValue: t('adminTesting.templatedEmail.composer.uploadFailed') }));
    } finally {
      setBusy(false);
    }
  };

  const inputClass = cn(
    'w-full px-3 py-2 rounded-lg border',
    'border-gray-300 dark:border-gray-600',
    'bg-white dark:bg-gray-700',
    primaryText, smallTextSize
  );
  const miniButtonClass = cn(
    'px-2 py-1 rounded border text-xs',
    'border-gray-300 dark:border-gray-600',
    'hover:bg-gray-100 dark:hover:bg-gray-700',
    secondaryText
  );

  const imagePicker = (opts: {
    url: string;
    alt: string;
    uploading: boolean;
    error: string | null;
    onFile: (file: File) => void;
    onAlt: (alt: string) => void;
    onRemove: () => void;
  }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          disabled={imagesBusy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) opts.onFile(file);
            e.target.value = '';
          }}
          className={cn(secondaryText, 'text-xs')}
        />
        {opts.uploading && (
          <span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.templatedEmail.composer.uploading')}</span>
        )}
      </div>
      {opts.url && (
        <img src={opts.url} alt="" className="max-h-40 rounded-lg border border-gray-200 dark:border-gray-600" />
      )}
      {opts.url.startsWith('data:') && (
        <p className={cn(secondaryText, 'text-xs')} role="status">{t('adminTesting.templatedEmail.composer.inlineFallback')}</p>
      )}
      {opts.error && <p className="text-xs text-red-600 dark:text-red-400" role="alert">{opts.error}</p>}
      {(opts.url || opts.error) && (
        <button type="button" disabled={imagesBusy} onClick={opts.onRemove} className={miniButtonClass}>
          {t('adminTesting.templatedEmail.composer.removeImage')}
        </button>
      )}
      <input
        type="text"
        value={opts.alt}
        onChange={(e) => opts.onAlt(e.target.value)}
        placeholder={t('adminTesting.templatedEmail.composer.altPlaceholder')}
        className={inputClass}
      />
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Language of the fixed strings (greeting/sign-off/footer) and of preset copy */}
      <div>
        <label className={cn(secondaryText, 'text-xs block mb-1')}>
          {t('adminTesting.templatedEmail.composer.languageLabel')}
        </label>
        <div className="flex gap-4">
          {(['en', 'nl'] as const).map((value) => (
            <label key={value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="product-update-language"
                checked={language === value}
                onChange={() => setLanguage(value)}
                className="w-4 h-4"
              />
              <span className={cn(smallTextSize, language === value ? primaryText : secondaryText)}>
                {t(value === 'en'
                  ? 'adminTesting.templatedEmail.composer.languageEn'
                  : 'adminTesting.templatedEmail.composer.languageNl')}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Preset picker — one named card per announced feature, copy applied in the chosen language */}
      <div>
        <label className={cn(secondaryText, 'text-xs block mb-1')}>
          {t('adminTesting.templatedEmail.composer.presetsLabel')}
        </label>
        <div className="flex flex-wrap gap-2">
          {PRODUCT_UPDATE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              disabled={imagesBusy}
              onClick={() => applyPreset(preset)}
              className={cn(
                'px-3 py-2 rounded-lg border text-left transition-colors',
                'border-gray-300 dark:border-gray-600',
                'hover:bg-gray-50 dark:hover:bg-gray-700'
              )}
            >
              <span className={cn(primaryText, smallTextSize, semiBoldText, 'block')}>{preset.name}</span>
              <span className={cn(secondaryText, 'text-xs block')}>{preset.copy[language].title}</span>
            </button>
          ))}
        </div>
        <p className={cn(secondaryText, 'text-xs mt-1')}>
          {t('adminTesting.templatedEmail.composer.presetsHint')}
        </p>
      </div>

      {/* Opening block: eyebrow + preheader, intro, feature link, hero image */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.templatedEmail.composer.eyebrowLabel')}
          </label>
          <input type="text" value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.templatedEmail.composer.preheaderLabel')}
          </label>
          <input
            type="text"
            value={preheader}
            onChange={(e) => setPreheader(e.target.value)}
            placeholder={t('adminTesting.templatedEmail.composer.preheaderPlaceholder')}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={cn(secondaryText, 'text-xs block mb-1')}>
          {t('adminTesting.templatedEmail.composer.introLabel')}
        </label>
        <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={3} className={inputClass} />
        <p className={cn(secondaryText, 'text-xs mt-1')}>{t('adminTesting.templatedEmail.composer.introHint')}</p>
      </div>

      <div>
        <label className={cn(secondaryText, 'text-xs block mb-1')}>
          {t('adminTesting.templatedEmail.composer.ctaUrlLabel')}
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            value={ctaUrl}
            onChange={(e) => setCtaUrl(e.target.value)}
            placeholder="https://culinarycloud.nl/features/…"
            className={inputClass}
          />
          <input
            type="text"
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
            placeholder={t('adminTesting.templatedEmail.composer.buttonLabelPlaceholder')}
            className={inputClass}
          />
        </div>
        <p className={cn(secondaryText, 'text-xs mt-1')}>{t('adminTesting.templatedEmail.composer.ctaUrlHint')}</p>
      </div>

      <div>
        <label className={cn(secondaryText, 'text-xs block mb-1')}>
          {t('adminTesting.templatedEmail.composer.heroImageLabel')}
        </label>
        {imagePicker({
          url: heroImageUrl,
          alt: heroImageAlt,
          uploading: heroUploading,
          error: heroUploadError,
          onFile: (file) => uploadImage(file, setHeroImageUrl, setHeroUploading, setHeroUploadError),
          onAlt: setHeroImageAlt,
          onRemove: () => { setHeroImageUrl(''); setHeroUploadError(null); },
        })}
      </div>

      {/* Chapters */}
      <div className="space-y-2">
        {chapters.map((chapter, index) => (
          <div key={chapter.key} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className={cn(primaryText, smallTextSize, semiBoldText)}>
                {t('adminTesting.templatedEmail.composer.chapterHeading', { number: index + 1 })}
              </span>
              <span className="flex gap-1">
                <button type="button" onClick={() => moveChapter(chapter.key, -1)} disabled={index === 0} className={cn(miniButtonClass, 'disabled:opacity-40')}>↑</button>
                <button type="button" onClick={() => moveChapter(chapter.key, 1)} disabled={index === chapters.length - 1} className={cn(miniButtonClass, 'disabled:opacity-40')}>↓</button>
                <button type="button" onClick={() => setChapters(prev => prev.filter(c => c.key !== chapter.key))} className={cn(miniButtonClass, 'text-red-500 dark:text-red-400')}>✕</button>
              </span>
            </div>

            <input
              type="text"
              value={chapter.title}
              onChange={(e) => updateChapter(chapter.key, { title: e.target.value })}
              placeholder={t('adminTesting.templatedEmail.composer.chapterTitlePlaceholder')}
              className={inputClass}
            />
            <textarea
              value={chapter.body}
              onChange={(e) => updateChapter(chapter.key, { body: e.target.value })}
              rows={3}
              placeholder={t('adminTesting.templatedEmail.composer.chapterBodyPlaceholder')}
              className={inputClass}
            />

            <div>
              <label className={cn(secondaryText, 'text-xs block mb-1')}>
                {t('adminTesting.templatedEmail.composer.listTypeLabel')}
              </label>
              <select
                value={chapter.listType}
                onChange={(e) => updateChapter(chapter.key, { listType: e.target.value as ListType })}
                className={inputClass}
              >
                {LIST_TYPES.map((value) => (
                  <option key={value} value={value}>{t(LIST_TYPE_LABEL_KEYS[value])}</option>
                ))}
              </select>
              {chapter.listType !== 'none' && (
                <textarea
                  value={chapter.itemsText}
                  onChange={(e) => updateChapter(chapter.key, { itemsText: e.target.value })}
                  rows={4}
                  placeholder={t(
                    chapter.listType === 'steps'
                      ? 'adminTesting.templatedEmail.composer.stepsPlaceholder'
                      : 'adminTesting.templatedEmail.composer.bulletsPlaceholder'
                  )}
                  className={cn(inputClass, 'mt-2')}
                />
              )}
            </div>

            <div>
              <label className={cn(secondaryText, 'text-xs block mb-1')}>
                {t('adminTesting.templatedEmail.composer.imageLabel')}
              </label>
              {imagePicker({
                url: chapter.imageUrl,
                alt: chapter.imageAlt,
                uploading: chapter.uploading,
                error: chapter.uploadError,
                onFile: (file) =>
                  uploadImage(
                    file,
                    (url) => updateChapter(chapter.key, { imageUrl: url }),
                    (busy) => updateChapter(chapter.key, { uploading: busy }),
                    (error) => updateChapter(chapter.key, { uploadError: error })
                  ),
                onAlt: (alt) => updateChapter(chapter.key, { imageAlt: alt }),
                onRemove: () => updateChapter(chapter.key, { imageUrl: '', uploadError: null }),
              })}
            </div>
          </div>
        ))}
      </div>

      {inlineImagesTooLarge && <p className="text-xs text-red-600 dark:text-red-400" role="alert">{t('adminTesting.templatedEmail.composer.inlineImagesTooLarge')}</p>}

      <button
        type="button"
        onClick={() => setChapters(prev => [...prev, emptyChapter(nextKey.current++)])}
        className={miniButtonClass}
      >
        {t('adminTesting.templatedEmail.composer.addChapter')}
      </button>
      <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.templatedEmail.composer.boldHint')}</p>

      {/* Closing panel — reuses the feature link above */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
        <span className={cn(primaryText, smallTextSize, semiBoldText, 'block mb-1')}>
          {t('adminTesting.templatedEmail.composer.closingLabel')}
        </span>
        <p className={cn(secondaryText, 'text-xs mb-2')}>{t('adminTesting.templatedEmail.composer.closingHint')}</p>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            value={closingHeadline}
            onChange={(e) => setClosingHeadline(e.target.value)}
            placeholder={t('adminTesting.templatedEmail.composer.closingHeadlinePlaceholder')}
            className={inputClass}
          />
          <input
            type="text"
            value={closingButtonLabel}
            onChange={(e) => setClosingButtonLabel(e.target.value)}
            placeholder={t('adminTesting.templatedEmail.composer.closingButtonPlaceholder')}
            className={inputClass}
          />
        </div>
      </div>
    </div>
  );
}
